import { useCallback, useEffect, useRef, useState } from 'react';

import type { DeliveryMethod, UsePhoneVerificationReturn, VerificationStep, VerificationStorage } from './types';

const STORAGE_KEY = 'ielect-phone-verification';
const OTP_COUNTDOWN_SECONDS = 300;
const RESEND_COOLDOWN_SECONDS = 30;
const DEMO_OTP = '555555';
const NIGERIAN_PHONE_REGEX = /^(?:\+234|0)?([789][01]\d{8})$/;

export const usePhoneVerification = (onVerificationComplete: (phoneNumber: string) => void): UsePhoneVerificationReturn => {
	const [step, setStep] = useState<VerificationStep>('phone');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('sms');
	const [countdown, setCountdown] = useState(0);
	const [resendCooldown, setResendCooldown] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const resendCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const clearCountdownInterval = useCallback(() => {
		if (countdownRef.current) {
			clearInterval(countdownRef.current);
			countdownRef.current = null;
		}
	}, []);

	const clearResendCooldownInterval = useCallback(() => {
		if (resendCooldownRef.current) {
			clearInterval(resendCooldownRef.current);
			resendCooldownRef.current = null;
		}
	}, []);

	useEffect(
		() => () => {
			clearCountdownInterval();
			clearResendCooldownInterval();
		},
		[clearCountdownInterval, clearResendCooldownInterval]
	);

	const startCountdown = useCallback(() => {
		clearCountdownInterval();
		setCountdown(OTP_COUNTDOWN_SECONDS);

		countdownRef.current = setInterval(() => {
			setCountdown((prev) => {
				if (prev <= 1) {
					clearCountdownInterval();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [clearCountdownInterval]);

	const startResendCooldown = useCallback(() => {
		clearResendCooldownInterval();
		setResendCooldown(RESEND_COOLDOWN_SECONDS);

		resendCooldownRef.current = setInterval(() => {
			setResendCooldown((prev) => {
				if (prev <= 1) {
					clearResendCooldownInterval();
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, [clearResendCooldownInterval]);

	const formatPhoneNumber = (phone: string): string => {
		const cleaned = phone.replace(/\D/g, '');
		if (cleaned.startsWith('234') && cleaned.length === 13) {
			return `0${cleaned.slice(3)}`;
		}
		if (cleaned.startsWith('0') && cleaned.length === 11) {
			return cleaned;
		}
		if (cleaned.length === 10 && /^[789]/.test(cleaned)) {
			return `0${cleaned}`;
		}
		return cleaned;
	};

	const validatePhoneNumber = (phone: string): boolean => {
		const formatted = formatPhoneNumber(phone);
		return NIGERIAN_PHONE_REGEX.test(formatted);
	};

	const saveVerification = useCallback((phone: string) => {
		const storage: VerificationStorage = {
			phoneNumber: phone,
			isVerified: true,
			verifiedAt: Date.now(),
			expiresAt: Date.now() + 24 * 60 * 60 * 1000,
		};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
	}, []);

	const sendCode = useCallback(() => {
		setError(null);

		const formattedPhone = formatPhoneNumber(phoneNumber);

		if (!validatePhoneNumber(formattedPhone)) {
			setError('Please enter a valid Nigerian phone number');
			return;
		}

		setIsLoading(true);

		setTimeout(() => {
			setIsLoading(false);
			setStep('otp');
			startCountdown();
			startResendCooldown();
		}, 1000);
	}, [phoneNumber, startCountdown, startResendCooldown]);

	const verifyCode = useCallback(
		(otpValue: string) => {
			setError(null);

			if (countdown === 0) {
				setError('Verification code has expired. Please request a new code');
				return;
			}

			if (otpValue.length !== 6) {
				setError('Please enter the complete 6-digit code');
				return;
			}

			setIsLoading(true);

			setTimeout(() => {
				setIsLoading(false);

				if (otpValue === DEMO_OTP) {
					const formattedPhone = formatPhoneNumber(phoneNumber);
					const fullPhone = formattedPhone.startsWith('0') ? `+234${formattedPhone.slice(1)}` : `+234${formattedPhone}`;

					saveVerification(fullPhone);
					clearCountdownInterval();
					clearResendCooldownInterval();
					onVerificationComplete(fullPhone);
				} else {
					setError('Incorrect code. Please check and try again, or click Resend Code');
				}
			}, 1000);
		},
		[countdown, phoneNumber, saveVerification, clearCountdownInterval, clearResendCooldownInterval, onVerificationComplete]
	);

	const resendCode = useCallback(() => {
		if (resendCooldown > 0) return;

		setError(null);
		setIsLoading(true);

		setTimeout(() => {
			setIsLoading(false);
			startCountdown();
			startResendCooldown();
		}, 1000);
	}, [resendCooldown, startCountdown, startResendCooldown]);

	const changePhone = useCallback(() => {
		clearCountdownInterval();
		clearResendCooldownInterval();
		setStep('phone');
		setError(null);
		setCountdown(0);
		setResendCooldown(0);
	}, [clearCountdownInterval, clearResendCooldownInterval]);

	const isExpired = step === 'otp' && countdown === 0;

	return {
		step,
		phoneNumber,
		setPhoneNumber,
		deliveryMethod,
		setDeliveryMethod,
		countdown,
		resendCooldown,
		isLoading,
		error,
		isExpired,
		sendCode,
		verifyCode,
		resendCode,
		changePhone,
	};
};

export const checkExistingVerification = (): VerificationStorage | null => {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) return null;

		const data: VerificationStorage = JSON.parse(stored);
		if (data.isVerified && Date.now() < data.expiresAt) {
			return data;
		}

		sessionStorage.removeItem(STORAGE_KEY);
		return null;
	} catch {
		return null;
	}
};
